import { Fragment } from 'react'

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

import { Button } from '@/components/common/Button/Button'
import { cn } from '@/utils/cn'

import type { TablePaginationProps } from '../DataTable.types'
import { getPaginationNumbers } from '../DataTable.utils'

export const TablePagination = <TData,>({
  table,
  pagination,
  showRowsPerPage = true,
  showInfo = true,
  className
}: TablePaginationProps<TData>) => {
  const {
    pageIndex,
    pageSize,
    total,
    pageCount,
    pageSizeOptions = [10, 20, 30, 40, 50],
    onPageChange,
    onPageSizeChange
  } = pagination

  const currentPageCount = pageCount || table.getPageCount()
  const selectedRowCount = table.getFilteredSelectedRowModel().rows.length
  const totalRowCount = table.getFilteredRowModel().rows.length

  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage)
    } else {
      table.setPageIndex(newPage)
    }
  }

  const handlePageSizeChange = (newSize: number) => {
    if (onPageSizeChange) {
      onPageSizeChange(newSize)
    } else {
      table.setPageSize(newSize)
    }
  }

  // Calculate display range
  const start = pageIndex * pageSize + 1
  const end = Math.min((pageIndex + 1) * pageSize, total || totalRowCount)
  const displayTotal = total || totalRowCount

  return (
    <div
      className={cn(
        'flex flex-col gap-4 px-2',
        'sm:flex-row sm:items-center sm:justify-between',
        className
      )}
    >
      {/* Selection and row info */}
      {showInfo && (
        <div className="flex-1 text-sm text-muted-foreground">
          <div className="flex flex-col gap-1 sm:flex-row sm:gap-4">
            {selectedRowCount > 0 && (
              <span>
                {selectedRowCount} de {totalRowCount} fila(s) seleccionada(s)
              </span>
            )}
            <span>
              Mostrando {start} a {end} de {displayTotal} registros
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-6 lg:gap-8">
        {/* Rows per page selector */}
        {showRowsPerPage && (
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Filas por página</p>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className={cn(
                'h-8 w-[70px] rounded-md border border-input bg-background px-3 py-1',
                'text-sm ring-offset-background',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Page navigation */}
        <div className="flex items-center space-x-2">
          {/* First page button */}
          <Button
            variant="secondary"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handlePageChange(0)}
            disabled={!table.getCanPreviousPage()}
            aria-label="Ir a la primera página"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          {/* Previous page button */}
          <Button
            variant="secondary"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handlePageChange(pageIndex - 1)}
            disabled={!table.getCanPreviousPage()}
            aria-label="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page numbers */}
          <div className="flex items-center space-x-1">
            {getPaginationNumbers(pageIndex, currentPageCount).map((page, index) => (
              <Fragment key={index}>
                {page === '...' ? (
                  <span className="flex h-8 w-8 items-center justify-center text-sm">...</span>
                ) : (
                  <Button
                    variant={page === pageIndex ? 'primary' : 'secondary'}
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => handlePageChange(page as number)}
                    aria-label={`Ir a la página ${(page as number) + 1}`}
                  >
                    {(page as number) + 1}
                  </Button>
                )}
              </Fragment>
            ))}
          </div>

          {/* Next page button */}
          <Button
            variant="secondary"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handlePageChange(pageIndex + 1)}
            disabled={!table.getCanNextPage()}
            aria-label="Página siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last page button */}
          <Button
            variant="secondary"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handlePageChange(currentPageCount - 1)}
            disabled={!table.getCanNextPage()}
            aria-label="Ir a la última página"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// Compact pagination for mobile
export const CompactPagination = <TData,>({
  table,
  pagination,
  className
}: Omit<TablePaginationProps<TData>, 'showRowsPerPage' | 'showInfo'>) => {
  const { pageIndex } = pagination
  const selectedRowCount = table.getFilteredSelectedRowModel().rows.length

  return (
    <div className={cn('flex items-center justify-between px-2', className)}>
      <div className="text-sm text-muted-foreground">
        {selectedRowCount > 0 && `${selectedRowCount} seleccionado(s) • `}
        Página {pageIndex + 1} de {table.getPageCount()}
      </div>

      <div className="flex items-center space-x-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          Anterior
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Siguiente
        </Button>
      </div>
    </div>
  )
}
